import * as NetworkStoreApi from '@gik/core/stores/NetworkStore.ts';
import type { APIResponseInterface } from '@gik/core/api/BaseAPIConfig';
import { DynamicComponent } from '@gik/core/components/DynamicComponent';
import { SkeletonNetworkError, SkeletonNetworkRetryMessage } from '@gik/core/components/NetworkError/NetworkError';
import type { bemFunction } from '@gik/core/utils/bemBlock';
import bemBlock from '@gik/core/utils/bemBlock';
import { useRandomInt } from '@gik/core/utils/getRandomInt';
import repeatElement from '@gik/core/utils/repeatElement';
import { Grid } from '@gik/ui-legacy/Grid';
import { UI } from '@gik/ui-legacy/UIManager';
import type React from 'react';

const blockName = 'saved-items-grid';

interface ISavedItemsGridProps<D, T = D> {
  itemName: string;
  optimisticIdsComparer(item: T): (id: string) => boolean;
  optimisticItemsComparer(item: T): (item: D) => boolean;
  removeCall(item: T): Promise<void>;
  emptyView?: React.ReactNode;
  skeleton: JSX.Element;
  fetchIds(): APIResponseInterface<string[]>;
  fetchItems(ids: string[]): APIResponseInterface<D[]>;
  transformItems?(items: D[]): T[];
  tileMapper(item: T, onClickRemove: (item: T) => Promise<void>): JSX.Element;
  requestKeyIds?: string;
  requestKeyItems?: string;
}

function SavedItemsGridComp<D, T = D>({
  itemName,
  optimisticIdsComparer,
  optimisticItemsComparer,
  removeCall,
  emptyView = null,
  skeleton: _skeleton,
  fetchIds,
  fetchItems,
  transformItems,
  tileMapper,
  requestKeyIds,
  requestKeyItems,
}: ISavedItemsGridProps<D, T>) {
  const bem = bemBlock(blockName);
  const repeat = useRandomInt(2, 6);

  function makeHandleDelete(ids: string[], items: D[], mutateIds, mutateItems) {
    return async function handleDelete(item: T) {
      if (await UI.confirm(`Are you sure you want to remove this ${itemName} from your favorites?`)) {
        const newIdsData = ids.filter(optimisticIdsComparer(item));
        const newItemsData = items.filter(optimisticItemsComparer(item));

        mutateIds(newIdsData, false);
        mutateItems(newItemsData, false);

        await removeCall(item);

        mutateIds();
        mutateItems();
      }
    };
  }

  const skeleton = () => <Content bem={bem}>{repeatElement(repeat, _skeleton)}</Content>;

  const skeletonWithError = (requestKey?: string, mutate?: () => void) => (
    <div className="relative">
      {skeleton()}
      {requestKey && NetworkStoreApi.shouldShowRetryMessage(requestKey) && !NetworkStoreApi.isError(requestKey) && (
        <SkeletonNetworkRetryMessage />
      )}
      {requestKey && NetworkStoreApi.isError(requestKey) && (
        <SkeletonNetworkError
          onReload={() => {
            NetworkStoreApi.reload(requestKey);
            mutate?.();
          }}
        />
      )}
    </div>
  );

  return (
    <DynamicComponent<string[]>
      fetch={fetchIds}
      loadingComponent={skeleton}
      requestKey={requestKeyIds}
      useNetworkStore={!!requestKeyIds}
    >
      {({ data: ids, mutate: mutateIds }) => {
        // Handle NetworkStore-based error state for IDs
        if (requestKeyIds && !NetworkStoreApi.isSuccess(requestKeyIds)) {
          return skeletonWithError(requestKeyIds, mutateIds);
        }

        return ids?.length == 0 ? (
          emptyView
        ) : (
          <DynamicComponent<D[], T[]>
            fetch={() => fetchItems(ids)}
            transform={transformItems}
            loadingComponent={skeleton}
            requestKey={requestKeyItems}
            useNetworkStore={!!requestKeyItems}
          >
            {({ data: items, transformed, mutate: mutateItems }) => {
              // Handle NetworkStore-based error state for Items
              if (requestKeyItems && !NetworkStoreApi.isSuccess(requestKeyItems)) {
                return skeletonWithError(requestKeyItems, mutateItems);
              }

              return transformed?.length == 0 ? (
                emptyView
              ) : (
                <Content bem={bem}>
                  {transformed?.map(data => tileMapper(data, makeHandleDelete(ids, items, mutateIds, mutateItems)))}
                </Content>
              );
            }}
          </DynamicComponent>
        );
      }}
    </DynamicComponent>
  );
}

export const SavedItemsGrid = SavedItemsGridComp;

interface IContentProps {
  bem: bemFunction;
}

function Content({ bem, children }: React.PropsWithChildren<IContentProps>) {
  return (
    <Grid cols={1} className={bem()}>
      {children}
    </Grid>
  );
}
